import * as React from "react";
import { useTranslation } from "react-i18next";
import { Box, Text } from "@advarra-engineering/advarra-design-system-react";
import { IconCheck, IconX } from "@tabler/icons-react";
import {
  PasswordRequirementChecks,
  PasswordRequirements,
} from "../@types/PasswordRequirements";

interface PasswordRequirementsProps {
  requirements: PasswordRequirements;
  checks: PasswordRequirementChecks;
}

/**
 * Renders a live-updating display of which password requirements are met by
 * the data entered by a user.
 *
 * @param {PasswordRequirementsProps} props The props.
 * @constructor
 */
export function PasswordRequirementChecker(
  props: PasswordRequirementsProps,
): React.JSX.Element {
  const { t } = useTranslation();
  const { requirements, checks } = props;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        flexGrow: 1,
        padding: theme.spacing.sm,
      })}
    >
      <Text size="md" sx={{ marginBottom: "10px" }}>
        {t("password-requirements.title")}
      </Text>
      <RequirementCheck
        data-testid="password-requirement-check-min-chars"
        compliant={props.checks.minLengthSatisfied}
        whenCompliant={t("password-requirements.characters-minimum", {
          count: props.requirements.minLength,
        })}
        whenNotCompliant={t("password-requirements.characters-minimum", {
          count: props.requirements.minLength,
        })}
      />
      {requirements.requireMixedCase && (
        <RequirementCheck
          data-testid="password-requirement-check-require-mixed-case"
          compliant={checks.mixedCaseSatisfied ?? false}
          whenCompliant={t("password-requirements.mixed-case")}
          whenNotCompliant={t("password-requirements.mixed-case")}
        />
      )}
      {requirements.requireNonAlpha && (
        <RequirementCheck
          data-testid="password-requirement-check-require-non-alpha"
          compliant={checks.nonAlphaSatisfied ?? false}
          whenCompliant={t("password-requirements.non-alpha")}
          whenNotCompliant={t("password-requirements.non-alpha")}
        />
      )}
      {requirements.requireNumber && (
        <RequirementCheck
          data-testid="password-requirement-require-number"
          compliant={checks.numberSatisfied ?? false}
          whenCompliant={t("password-requirements.number")}
          whenNotCompliant={t("password-requirements.number")}
        />
      )}
      <RequirementCheck
        data-testid="password-requirement-check-passwords-match"
        compliant={props.checks.equal}
        whenCompliant={t("password-requirements.passwords-match")}
        whenNotCompliant={t("password-requirements.passwords-do-not-match")}
      />
    </Box>
  );
}

interface RequirementCheckProps {
  "data-testid": string;
  compliant: boolean;
  whenCompliant: string;
  whenNotCompliant: string;
}

/**
 * Renders a given line item for a requirement.
 * @param {RequirementCheckProps} props The props
 * @constructor
 */
function RequirementCheck(props: RequirementCheckProps): React.JSX.Element {
  const color = props.compliant
    ? "var(--adsColorSuccessDefault, #7CB721)"
    : "var(--adsColorDangerDefault, #C60C30)";
  const icon = props.compliant ? <IconCheck /> : <IconX />;
  const text = props.compliant ? props.whenCompliant : props.whenNotCompliant;

  return (
    <Text
      data-testid={props["data-testid"]}
      data-color={color}
      size="sm"
      c={color}
      sx={{ display: "flex", alignItems: "center", gap: "5px" }}
    >
      <span style={{ flexShrink: 0 }}>{icon}</span>
      {text}
    </Text>
  );
}
